/**
 * AdminInventoryPage ✦ iter223 — /admin/inventory
 *
 * Single-screen control surface for the small fleet. Each row
 * shows status dot + name, total stock (editable), active bookings
 * with one-click −/+ pair (mirrors backend confirm/release),
 * live "Available" computed from total − active, display order
 * input + Active/Inactive toggle. Soft-delete only.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SERIF = 'Georgia, "Cormorant Garamond", "Times New Roman", serif';
const GOLD = '#D4AF5E';
const GOLD_FAINT = 'rgba(212,170,82,0.22)';
const IVORY = '#FBEED0';
const IVORY_DIM = 'rgba(251,238,208,0.62)';
const IVORY_GHOST = 'rgba(251,238,208,0.42)';
const DOT_COLOR = { green: '#7DB58C', amber: '#E2C77B', orange: '#E39A4F', red: '#B86F6F' };

const fieldStyle = {
  background: 'rgba(0,0,0,0.32)', border: '1px solid rgba(255,255,255,0.10)',
  borderRadius: 8, color: IVORY, fontFamily: SERIF, fontSize: 13.5,
  padding: '7px 10px', width: 70, textAlign: 'center',
};

const pillBtn = (active) => ({
  padding: '6px 14px', borderRadius: 999,
  border: `1px solid ${active ? GOLD : 'rgba(255,255,255,0.14)'}`,
  background: active ? 'rgba(212,170,82,0.18)' : 'rgba(255,255,255,0.04)',
  color: active ? GOLD : IVORY_DIM,
  fontFamily: SERIF, fontStyle: 'italic', fontSize: 12.5, cursor: 'pointer',
});

const AdminInventoryPage = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  // iter225 — utilisation reporting at the top of the page.
  const currentYear = new Date().getFullYear();
  const [utilWindow, setUtilWindow] = useState('calendar');  // 'calendar' | 'rolling'
  const [utilYear, setUtilYear] = useState(currentYear);
  const [utilisation, setUtilisation] = useState(null);
  const [utilLoading, setUtilLoading] = useState(false);

  useEffect(() => {
    if (auth?.user === null) navigate('/login?next=/admin/inventory');
  }, [auth?.user, navigate]);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/inventory`);
      setItems(r.data.items || []);
    } catch (e) {
      if (e?.response?.status !== 401) toast.error('Could not load inventory.');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadUtilisation = useCallback(async () => {
    setUtilLoading(true);
    try {
      const params = utilWindow === 'rolling'
        ? { window: 'rolling' }
        : { window: 'calendar', year: utilYear };
      const r = await axios.get(`${API}/admin/utilisation`, { params });
      setUtilisation(r.data);
    } catch (e) {
      if (e?.response?.status !== 401) {
        // Quiet failure — utilisation is a non-critical surface.
      }
      setUtilisation(null);
    } finally {
      setUtilLoading(false);
    }
  }, [utilWindow, utilYear]);

  useEffect(() => { loadUtilisation(); }, [loadUtilisation]);

  useEffect(() => { load(); }, [load]);

  const patch = async (slug, body) => {
    try { await axios.patch(`${API}/admin/inventory/${slug}`, body); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Update failed.'); }
  };
  const confirmBooking = async (slug) => {
    try { await axios.post(`${API}/admin/inventory/${slug}/booking`); toast.success('Booking confirmed ✦'); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Confirm failed.'); }
  };
  const releaseBooking = async (slug) => {
    try { await axios.delete(`${API}/admin/inventory/${slug}/booking`); toast.success('Booking released ✦'); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Release failed.'); }
  };
  const deleteItem = async (slug, name) => {
    if (!window.confirm(`Permanently delete "${name}"?\n\nThis removes one matching row from inventory. Active bookings that reference this item will keep their record but the item will no longer count toward availability.`)) return;
    try {
      const r = await axios.delete(`${API}/admin/inventory/${slug}`);
      toast.success(`Removed ✦ (${r.data?.deleted_count || 1} row)`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed.');
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#0F0B1A', color: IVORY, fontFamily: SERIF, padding: '80px 24px', textAlign: 'center' }}>
        <p style={{ fontStyle: 'italic', color: IVORY_DIM }}>Loading inventory ✦</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-inventory-page" style={{
      minHeight: '100vh', background: '#0F0B1A', color: IVORY, fontFamily: SERIF,
      padding: 'clamp(40px, 6vw, 70px) clamp(16px, 3vw, 40px) 80px',
    }}>
      <div style={{ maxWidth: 980, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 16, marginBottom: 32 }}>
          <div>
            <div style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 11, letterSpacing: '0.28em', textTransform: 'uppercase', color: GOLD, marginBottom: 8 }}>✦ Inventory &amp; Availability</div>
            <h1 style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 'clamp(26px, 4vw, 36px)', margin: 0, letterSpacing: '-0.015em' }}>Live stock control</h1>
            <p style={{ fontFamily: SERIF, fontStyle: 'italic', color: IVORY_DIM, fontSize: 13.5, margin: '8px 0 0', maxWidth: 600, lineHeight: 1.55 }}>
              Edit total stock at any time. When a booking confirms, tap <strong style={{ color: GOLD }}>+</strong>; if it cancels, tap <strong style={{ color: GOLD }}>−</strong>. The customer-facing availability strip updates instantly.
            </p>
          </div>
          <button type="button" onClick={() => navigate('/admin')} data-testid="admin-back-link"
            style={{ background: 'transparent', border: `1px solid ${GOLD_FAINT}`, color: IVORY_DIM, fontFamily: SERIF, fontStyle: 'italic', fontSize: 13, padding: '8px 18px', borderRadius: 999, cursor: 'pointer' }}
          >← Admin home</button>
        </div>

        <UtilisationPanel
          utilisation={utilisation}
          loading={utilLoading}
          windowMode={utilWindow}
          onWindowChange={setUtilWindow}
          year={utilYear}
          onYearChange={setUtilYear}
          currentYear={currentYear}
        />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {items.map((it) => (
            <InventoryRow key={`${it.slug}-${it._id || ''}`} item={it}
              onPatch={(body) => patch(it.slug, body)}
              onConfirm={() => confirmBooking(it.slug)}
              onRelease={() => releaseBooking(it.slug)}
              onDelete={() => deleteItem(it.slug, it.name)}
            />
          ))}
        </div>

        <div style={{ marginTop: 36, padding: '22px 24px', border: `1px dashed ${GOLD_FAINT}`, borderRadius: 12 }}>
          {creating ? (
            <NewItemForm onCancel={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />
          ) : (
            <button type="button" onClick={() => setCreating(true)} data-testid="add-inventory-item"
              style={{ background: 'transparent', border: 'none', color: GOLD, fontFamily: SERIF, fontStyle: 'italic', fontSize: 14, cursor: 'pointer' }}
            >+ Add a new item</button>
          )}
        </div>
      </div>
    </div>
  );
};

const InventoryRow = ({ item, onPatch, onConfirm, onRelease, onDelete }) => {
  const [total, setTotal] = useState(item.total_stock);
  const [order, setOrder] = useState(item.display_order);
  useEffect(() => { setTotal(item.total_stock); setOrder(item.display_order); }, [item]);

  const labelStyle = { fontFamily: SERIF, fontStyle: 'italic', fontSize: 10.5, color: IVORY_DIM, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 4 };
  const stepperBtn = (enabled, isPlus) => ({
    width: 28, height: 28, borderRadius: 999,
    border: `1px solid ${enabled && isPlus ? GOLD : 'rgba(255,255,255,0.14)'}`,
    background: enabled && isPlus ? 'rgba(212,170,82,0.18)' : 'transparent',
    color: enabled ? (isPlus ? GOLD : IVORY) : IVORY_DIM,
    cursor: enabled ? 'pointer' : 'not-allowed',
    opacity: enabled ? 1 : 0.4, fontFamily: SERIF, fontSize: 14, lineHeight: 1,
  });

  // iter227.1 — flex+wrap instead of a rigid 6-col grid so the row
  // collapses cleanly on mobile (Craig hit horizontal overflow on
  // production). The name stretches; controls flow underneath
  // when there's no room beside it.
  return (
    <div data-testid={`inventory-row-${item.slug}`} data-status={item.status} style={{
      display: 'flex', flexWrap: 'wrap', alignItems: 'center',
      gap: 16, rowGap: 14, padding: '14px 18px',
      background: item.is_active ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.015)',
      border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12,
      opacity: item.is_active ? 1 : 0.55,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: '1 1 240px', minWidth: 0 }}>
        <span aria-hidden style={{ width: 10, height: 10, borderRadius: 999, background: DOT_COLOR[item.status], boxShadow: `0 0 10px ${DOT_COLOR[item.status]}99`, flexShrink: 0 }} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontFamily: SERIF, fontSize: 15, color: IVORY, fontWeight: 500, lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.name}</div>
          <div style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 11.5, color: IVORY_DIM, letterSpacing: '0.04em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.slug} · {item.category}</div>
        </div>
      </div>

      <label style={{ textAlign: 'center', flex: '0 0 auto' }}>
        <div style={labelStyle}>Total</div>
        <input type="number" min={0} max={99} value={total}
          onChange={(e) => setTotal(Number(e.target.value))}
          onBlur={() => { if (total !== item.total_stock) onPatch({ total_stock: total }); }}
          data-testid={`inventory-total-${item.slug}`} style={fieldStyle} />
      </label>

      <div style={{ textAlign: 'center', flex: '0 0 auto' }}>
        <div style={labelStyle}>On Booking</div>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <button type="button" onClick={onRelease} disabled={item.active_bookings <= 0}
            data-testid={`inventory-release-${item.slug}`}
            style={stepperBtn(item.active_bookings > 0, false)}>−</button>
          <span style={{ fontFamily: SERIF, fontSize: 15, color: IVORY, minWidth: 20, textAlign: 'center' }} data-testid={`inventory-active-${item.slug}`}>{item.active_bookings}</span>
          <button type="button" onClick={onConfirm} disabled={item.active_bookings >= item.total_stock}
            data-testid={`inventory-confirm-${item.slug}`}
            style={stepperBtn(item.active_bookings < item.total_stock, true)}>+</button>
        </div>
      </div>

      <div style={{ textAlign: 'center', flex: '0 0 auto' }}>
        <div style={labelStyle}>Available</div>
        <div style={{ fontFamily: SERIF, fontSize: 18, fontWeight: 600, color: DOT_COLOR[item.status] }} data-testid={`inventory-available-${item.slug}`}>
          {item.available_stock}
        </div>
      </div>

      <label style={{ textAlign: 'center', flex: '0 0 auto' }}>
        <div style={labelStyle}>Order</div>
        <input type="number" min={0} max={9999} value={order}
          onChange={(e) => setOrder(Number(e.target.value))}
          onBlur={() => { if (order !== item.display_order) onPatch({ display_order: order }); }}
          data-testid={`inventory-order-${item.slug}`} style={{ ...fieldStyle, width: 70 }} />
      </label>

      <div style={{ display: 'flex', gap: 8, flex: '0 0 auto', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        <button type="button"
          onClick={() => onPatch({ is_active: !item.is_active })}
          data-testid={`inventory-toggle-active-${item.slug}`}
          style={pillBtn(item.is_active)}
        >{item.is_active ? '✓ Active' : 'Inactive'}</button>

        <button type="button"
          onClick={onDelete}
          data-testid={`inventory-delete-${item.slug}`}
          aria-label={`Delete ${item.name}`}
          title="Permanently remove this item"
          style={{
            padding: '6px 12px', borderRadius: 999,
            border: '1px solid rgba(184,111,111,0.36)',
            background: 'transparent',
            color: 'rgba(230,181,181,0.95)',
            fontFamily: SERIF, fontStyle: 'italic', fontSize: 12, cursor: 'pointer',
            letterSpacing: '0.04em', whiteSpace: 'nowrap',
          }}
        >Delete</button>
      </div>
    </div>
  );
};

const NewItemForm = ({ onCancel, onSaved }) => {
  const [form, setForm] = useState({ slug: '', name: '', category: 'booth', total_stock: 1, display_order: 100 });
  const [saving, setSaving] = useState(false);
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    if (!form.slug || !form.name) { toast.error('Slug and name are required.'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/inventory`, { ...form, total_stock: Number(form.total_stock), display_order: Number(form.display_order) });
      toast.success('Item added ✦');
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Create failed.');
    } finally { setSaving(false); }
  };

  const inp = { ...fieldStyle, width: '100%' };
  return (
    <div data-testid="new-inventory-form" style={{ display: 'grid', gap: 12 }}>
      <div style={{ fontFamily: SERIF, fontStyle: 'italic', color: GOLD, fontSize: 12, letterSpacing: '0.22em', textTransform: 'uppercase' }}>Add a new item</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
        <input placeholder="slug (kebab-case)" value={form.slug} onChange={update('slug')} style={inp} data-testid="new-inv-slug" />
        <input placeholder="Display name" value={form.name} onChange={update('name')} style={inp} data-testid="new-inv-name" />
        <select value={form.category} onChange={update('category')} style={inp} data-testid="new-inv-category">
          <option value="booth">Booth</option>
          <option value="guestbook">Guestbook</option>
          <option value="letters">Letters</option>
          <option value="backdrop">Backdrop</option>
          <option value="other">Other</option>
        </select>
        <input type="number" min={0} value={form.total_stock} onChange={update('total_stock')} style={inp} data-testid="new-inv-total" />
        <input type="number" min={0} value={form.display_order} onChange={update('display_order')} style={inp} data-testid="new-inv-order" />
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        <button type="button" onClick={submit} disabled={saving} data-testid="new-inv-save"
          style={{ padding: '8px 18px', borderRadius: 999, border: 'none', background: GOLD, color: '#0F0B1A', fontFamily: SERIF, fontWeight: 600, fontSize: 13, cursor: saving ? 'wait' : 'pointer' }}
        >{saving ? 'Saving…' : 'Add item'}</button>
        <button type="button" onClick={onCancel}
          style={{ padding: '8px 18px', borderRadius: 999, border: '1px solid rgba(255,255,255,0.14)', background: 'transparent', color: IVORY_DIM, fontFamily: SERIF, fontStyle: 'italic', fontSize: 13, cursor: 'pointer' }}
        >Cancel</button>
      </div>
    </div>
  );
};

export default AdminInventoryPage;

// ─── iter225 · Utilisation panel ──────────────────────────────────
const UtilisationPanel = ({
  utilisation, loading, windowMode, onWindowChange,
  year, onYearChange, currentYear,
}) => {
  const totals = utilisation?.totals;
  const rows = utilisation?.items || [];

  const togglePill = (active) => ({
    padding: '6px 14px', borderRadius: 999,
    border: `1px solid ${active ? GOLD : 'rgba(255,255,255,0.14)'}`,
    background: active ? 'rgba(212,170,82,0.18)' : 'rgba(255,255,255,0.04)',
    color: active ? GOLD : IVORY_DIM,
    fontFamily: SERIF, fontStyle: 'italic', fontSize: 12.5, cursor: 'pointer',
  });

  const years = [];
  for (let y = currentYear + 1; y >= 2024; y--) years.push(y);

  return (
    <section data-testid="utilisation-panel" style={{
      margin: '0 0 36px',
      padding: 'clamp(22px, 3vw, 30px)',
      border: `1px solid ${GOLD_FAINT}`,
      borderRadius: 14,
      background: 'rgba(212,170,82,0.04)',
    }}>
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'baseline',
        justifyContent: 'space-between', marginBottom: 18,
      }}>
        <div>
          <div style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 11,
            letterSpacing: '0.28em', textTransform: 'uppercase', color: GOLD, marginBottom: 6 }}>
            ✦ Utilisation Reporting
          </div>
          <h2 style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 'clamp(20px, 3vw, 26px)',
            margin: 0, color: IVORY, letterSpacing: '-0.005em' }}>
            What&apos;s earning its keep · {utilisation?.label || '—'}
          </h2>
          <p style={{ fontFamily: SERIF, fontStyle: 'italic', color: IVORY_DIM,
            fontSize: 13, margin: '6px 0 0', lineHeight: 1.5 }}>
            Bookings and revenue per asset for {windowMode === 'rolling' ? 'the trailing twelve months' : `${year}`}.
            Only confirmed and completed bookings count. Use this to decide where to expand stock next.
          </p>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <button type="button" onClick={() => onWindowChange('calendar')}
            data-testid="util-window-calendar"
            style={togglePill(windowMode === 'calendar')}>Calendar Year</button>
          <button type="button" onClick={() => onWindowChange('rolling')}
            data-testid="util-window-rolling"
            style={togglePill(windowMode === 'rolling')}>Rolling 12m</button>
          {windowMode === 'calendar' && (
            <select value={year} onChange={(e) => onYearChange(Number(e.target.value))}
              data-testid="util-year-select"
              style={{
                background: 'rgba(0,0,0,0.32)', border: '1px solid rgba(255,255,255,0.14)',
                borderRadius: 999, padding: '6px 14px',
                color: IVORY, fontFamily: SERIF, fontSize: 12.5, cursor: 'pointer',
              }}>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          )}
        </div>
      </div>

      {loading ? (
        <p style={{ fontFamily: SERIF, fontStyle: 'italic', color: IVORY_DIM, textAlign: 'center' }}>
          Crunching the numbers…
        </p>
      ) : rows.length === 0 ? (
        <p style={{ fontFamily: SERIF, fontStyle: 'italic', color: IVORY_DIM, textAlign: 'center', padding: '20px 0' }}>
          No confirmed bookings in this window yet. Numbers populate as bookings move to Confirmed.
        </p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table data-testid="utilisation-table" style={{
            width: '100%', borderCollapse: 'collapse',
            fontFamily: SERIF, fontSize: 14, color: IVORY,
          }}>
            <thead>
              <tr style={{ textAlign: 'left' }}>
                {['Asset', 'Owned', 'Bookings', 'Revenue', 'Utilisation'].map((h) => (
                  <th key={h} style={{
                    padding: '8px 12px',
                    borderBottom: `1px solid ${GOLD_FAINT}`,
                    fontFamily: SERIF, fontStyle: 'italic',
                    fontSize: 10.5, letterSpacing: '0.18em', textTransform: 'uppercase',
                    color: IVORY_DIM, fontWeight: 500,
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.slug} data-testid={`utilisation-row-${r.slug}`}>
                  <td style={cellStyle}>{r.name}</td>
                  <td style={cellStyle}>{r.total_stock}</td>
                  <td style={{ ...cellStyle, color: GOLD, fontWeight: 600 }}>{r.bookings}</td>
                  <td style={cellStyle}>£{r.revenue_pounds.toLocaleString('en-GB')}</td>
                  <td style={cellStyle}>
                    <span data-testid={`utilisation-pct-${r.slug}`}>{r.utilisation_pct}%</span>
                    <div style={{
                      marginTop: 4, height: 4, background: 'rgba(255,255,255,0.06)',
                      borderRadius: 2, overflow: 'hidden', maxWidth: 120,
                    }}>
                      <div style={{
                        width: `${Math.min(100, r.utilisation_pct)}%`,
                        height: '100%',
                        background: r.utilisation_pct >= 60 ? '#7DB58C' : r.utilisation_pct >= 30 ? '#E2C77B' : '#E39A4F',
                      }} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            {totals && (
              <tfoot>
                <tr>
                  <td colSpan={2} style={{ ...cellStyle, color: IVORY_DIM, fontStyle: 'italic' }}>
                    Total
                  </td>
                  <td style={{ ...cellStyle, color: GOLD, fontWeight: 600 }} data-testid="utilisation-total-bookings">
                    {totals.bookings}
                  </td>
                  <td style={{ ...cellStyle, color: GOLD, fontWeight: 600 }} data-testid="utilisation-total-revenue">
                    £{totals.revenue_pounds.toLocaleString('en-GB')}
                  </td>
                  <td style={cellStyle}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
      <p style={{ fontFamily: SERIF, fontStyle: 'italic', color: IVORY_GHOST,
        fontSize: 11.5, margin: '14px 0 0', lineHeight: 1.5, textAlign: 'center' }}>
        Utilisation = bookings ÷ (stock × weekend days in window). Anything above 60% is a strong &quot;buy another&quot; signal.
      </p>
    </section>
  );
};

const cellStyle = {
  padding: '12px 12px',
  borderBottom: `1px solid rgba(255,255,255,0.05)`,
  fontFamily: SERIF, fontSize: 14, color: IVORY,
  verticalAlign: 'middle',
};
